import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import axios from "axios";
import { postWithRetry } from "../tools/postWithRetry";
import { getDownloadURL, ref } from "firebase/storage";
import { storage } from "../tools/firebase";

const SupplierRetailerContext = createContext();

export const useSupplierRetailer = () => useContext(SupplierRetailerContext);

const ORG_KEY = "spearbi_org";
const DEFAULT_ORG = "SPEARMGMT";
// A supplier may belong to several organisations (2026-09-17): `orgs` lists them
// all (home first) and `org` is the home one; rows from an older backend carry
// only `org`. A supplier with no organisation at all stays visible everywhere.
const supplierInOrg = (s, org_code) => {
  const orgs = Array.isArray(s.orgs) && s.orgs.length ? s.orgs : s.org ? [s.org] : [];
  return orgs.length === 0 || orgs.includes(org_code);
};
// Master admins only — same hardcoded pair as AuthenticatedLayout/Sidebar.
const isMasterAdmin = () => {
  const userid = localStorage.getItem("userid");
  return userid === "1" || userid === "2";
};

export const SupplierRetailerProvider = ({ children }) => {
  const [selectedRetailer, setSelectedRetailer] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState("");
  // "All suppliers" in the left-hand picker — offered on the rep's route page only (Michael 2026-10-07). The
  // selected supplier underneath is left untouched; picking a supplier switches this off.
  const [allSuppliersView, setAllSuppliersView] = useState(true);
  const [organisations, setOrganisations] = useState([]);
  const [selectedOrg, setSelectedOrgState] = useState(() => localStorage.getItem(ORG_KEY) || DEFAULT_ORG);
  const [userOrg, setUserOrg] = useState(null); // caller's own org: { org_code, org_name } | null
  // Organisations the caller may open the Rep page for (2026-09-21) — [{org_code, org_name}].
  // Masters: every org with rep→store assignments; everyone else: their own org when it has
  // them. Drives the nav item; the backend (requireRepOrg) enforces the same rule.
  const [repOrgs, setRepOrgs] = useState([]);
  const [retailers, setRetailers] = useState([
    { value: "PNP", text: "PnP" },
    { value: "CHK", text: "Checkers" },
    { value: "MED", text: "MediRite" },
    { value: "PET", text: "Petshop" },
    { value: "DIS", text: "Dischem" },
    { value: "CLI", text: "Clicks" },
    { value: "MAK", text: "Makro" },
  ]);
  const [all_supplier_access, setAll_supplier_access] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableRetailers, setAvailableRetailers] = useState({});
  const [retailerLogos, setRetailerLogos] = useState({});

  // Master admins see the app scoped to one organisation at a time. Suppliers
  // without an org (rows carry `org` from the backend; null until phase 0 runs
  // on that DB) always stay visible, so this is a no-op on prod pre-migration.
  const user_supplier_access = useMemo(() => {
    if (!isMasterAdmin() || !selectedOrg) return all_supplier_access;
    return all_supplier_access.filter((s) => supplierInOrg(s, selectedOrg));
  }, [all_supplier_access, selectedOrg]);

  // Supplier-level subscription block (2026-09-09). Each row from
  // /login/get--user-access carries `unpaid: true` when that supplier's OWNER
  // account is suspended — the master's Restr toggle, or no payment for the
  // month after the 7th. The shell replaces the report pages for that supplier
  // with "<supplier> account suspended" for EVERY account except the two master
  // admins (userid 1/2) — AuthenticatedLayout; the backend refuses the same
  // calls with 403 SUPPLIER_SUSPENDED (middleware/requireSupplierPaid).
  const selectedSupplierRow = useMemo(
    () => all_supplier_access.find((s) => s.value === selectedSupplier) || null,
    [all_supplier_access, selectedSupplier],
  );
  const supplierUnpaid = !!(selectedSupplierRow && selectedSupplierRow.unpaid);
  const supplierBlocked = supplierUnpaid && !isMasterAdmin();

  const setSelectedOrg = (org_code) => {
    localStorage.setItem(ORG_KEY, org_code);
    setSelectedOrgState(org_code);
    // If the current supplier belongs to another org, jump to the first
    // supplier inside the newly selected one.
    const visible = all_supplier_access.filter((s) => supplierInOrg(s, org_code));
    if (visible.length && !visible.some((s) => s.value === selectedSupplier)) {
      setSelectedSupplier(visible[0].value);
    }
  };

  const fetchOrganisations = async () => {
    if (!isMasterAdmin()) return;
    try {
      const userid = localStorage.getItem("userid");
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/get-organisations`, { userid });
      setOrganisations(response.data?.data || []);
    } catch (error) {
      // Pre-migration DBs have no organisations table — the toggle simply stays hidden.
      console.error("Error fetching organisations:", error);
    }
  };

  const fetchRepAvailability = async () => {
    try {
      const userid = localStorage.getItem("userid");
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/rep_report/availability`, { userid });
      setRepOrgs(response.data?.data?.orgs || []);
    } catch (error) {
      // An older backend has no such route — the Rep item simply stays hidden.
      setRepOrgs([]);
    }
  };

  const fetchUserAccess = async (userid) => {
    try {
      setLoading(true);
      const response = await postWithRetry(`${process.env.REACT_APP_API_URL}/login/get--user-access`, { userid });
      const { user_supplier_access, user_defaults, org } = response.data.data;

      setUserOrg(org || null);
      // The sidebar brand mark reads these synchronously (tools/brandMark.js).
      if (org && org.org_code) {
        localStorage.setItem("user_org_code", org.org_code);
        localStorage.setItem("user_org_name", org.org_name || org.org_code);
      } else {
        localStorage.removeItem("user_org_code");
        localStorage.removeItem("user_org_name");
      }
      setAll_supplier_access(user_supplier_access);
      // A master admin reloading while toggled into another org must not boot
      // into their default supplier if it sits outside that org — reconcile the
      // initial supplier against the persisted org scope.
      let initialSupplier = user_defaults.default_supplier;
      // Everyone else: a missing default (an org admin created before its
      // organisation had a supplier — 2026-09-16) or one no longer granted
      // used to open a blank app; fall back to the first granted supplier.
      const scopeOrg = isMasterAdmin() ? localStorage.getItem(ORG_KEY) || DEFAULT_ORG : null;
      const visible = scopeOrg ? user_supplier_access.filter((s) => supplierInOrg(s, scopeOrg)) : user_supplier_access;
      if (visible.length && !visible.some((s) => s.value === initialSupplier)) {
        initialSupplier = visible[0].value;
      }
      setSelectedSupplier(initialSupplier);
      setSelectedRetailer(user_defaults.default_retailer);
      fetchOrganisations();
      fetchRepAvailability();
      return true;
    } catch (error) {
      console.error("Error fetching user access:", error);
      return false;
    } finally {
      setLoading(false);
    }
  };
  // Re-pull the supplier LIST only — unlike fetchUserAccess this never touches
  // the current supplier/retailer selection or the loading flag, so it is safe
  // to call mid-session after an admin action changes which suppliers exist
  // (creating or deleting a supplier). Silent on failure: the list simply
  // stays stale until the next full app load.
  const refreshSupplierList = async () => {
    try {
      const userid = localStorage.getItem("userid");
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/login/get--user-access`, { userid });
      setAll_supplier_access(response.data.data.user_supplier_access);
    } catch (error) {
      console.error("Error refreshing supplier list:", error);
    }
  };
  const fetchSupplierAccess = async () => {
    try {
      const userid=localStorage.getItem("userid");
      setLoading(true);
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/admin/get-supplier-access`, { supplier: selectedSupplier,userid });
      setAvailableRetailers(response.data.data);
      const firstAvailableRetailer = Object.keys(response.data.data).find((key) => response.data.data[key]);
      if (firstAvailableRetailer) {
        setSelectedRetailer(firstAvailableRetailer);
      }
    } catch (error) {
      console.error("Error fetching user access:", error);
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   const fetchLogos = async () => {
  //     const logoUrls = {};
  //     const retailerValues = ["PNP", "CHK", "DIS", "CLI", "MAK"];
  //     for (const value of retailerValues) {
  //       const logoRef = ref(storage, `retailers/${value.toLowerCase()}.jpg`);
  //       try {
  //         const url = await getDownloadURL(logoRef);
  //         logoUrls[value] = url;
  //       } catch (error) {
  //         console.error("Error fetching logo:", error);
  //         // Optionally use a default logo if there is an error fetching the retailer logo
  //       }
  //     }
  //     setRetailerLogos(logoUrls);
  //   };

  //   fetchLogos();
  // }, []);

  const resetContext = () => {
    setAll_supplier_access([]);
    setOrganisations([]);
    setUserOrg(null);
    setRepOrgs([]);
    setSelectedRetailer("");
    setSelectedSupplier("");
    setLoading(false);
    setAvailableRetailers({});
    // setRetailerLogos({});
  };

  useEffect(() => {
    setAvailableRetailers({});
    fetchSupplierAccess();
  }, [selectedSupplier]);

  return (
    <SupplierRetailerContext.Provider
      value={{
        selectedRetailer,
        setSelectedRetailer,
        selectedSupplier,
        setSelectedSupplier,
        allSuppliersView,
        setAllSuppliersView,
        retailers,
        user_supplier_access,
        loading,
        fetchUserAccess,
        resetContext,
        availableRetailers,
        fetchSupplierAccess,
        refreshSupplierList,
        organisations,
        selectedOrg,
        setSelectedOrg,
        fetchOrganisations,
        userOrg,
        repOrgs,
        // The Rep page exists for the organisation in view: the master's toggle, or the caller's own.
        repEnabled: isMasterAdmin() ? repOrgs.some((o) => o.org_code === selectedOrg) : repOrgs.length > 0,
        isMaster: isMasterAdmin(),
        selectedSupplierRow,
        supplierUnpaid,   // fact: the selected supplier's account is blocked (shown to masters too)
        supplierBlocked,  // gate: unpaid AND the caller is not a master
        // retailerLogos,
      }}
    >
      {children}
    </SupplierRetailerContext.Provider>
  );
};
